import { useState } from 'react';
import { Phone, Search } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { usePermissions } from '@/hooks/use-permissions';
import { PERMISSION } from '@/features/users-roles/constants';
import type { IContact } from '@/features/contacts/types';

import { useWidgetContacts } from '../../hooks/use-widget-contacts';
import { useVoiceStore } from '../../state/voice-ui.store';
import { CallerIdentity } from './caller-identity';

/** How close to the bottom (px) before the next page loads. */
const LOAD_MORE_THRESHOLD_PX = 80;

function contactName(contact: Pick<IContact, 'firstName' | 'lastName'>): string | null {
    return [contact.firstName, contact.lastName].filter(Boolean).join(' ') || null;
}

interface ContactsPanelProps {
    readonly onCall: (toNumber: string) => void;
    /** True when the widget has an explicit (resized) size — the list then
     *  fills the stretched body instead of capping at the design max-height. */
    readonly fill?: boolean;
}

/**
 * Widget Contacts tab — the org's contact book, narrowed by a name/number
 * search; a row click dials the contact's saved number. Rows are guarded
 * while a call is active (finish-first), same as History redial.
 */
export function ContactsPanel({ onCall, fill = false }: ContactsPanelProps) {
    const { hasAnyPermission, isLoading: permissionsLoading } = usePermissions();
    const canReadContacts = hasAnyPermission([PERMISSION.CONTACTS_READ]);

    const activeCall = useVoiceStore((s) => s.activeCall);

    const [query, setQuery] = useState('');
    const search = useDebouncedValue(query.trim());

    const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useWidgetContacts({
        search: search || undefined,
        enabled: !permissionsLoading && canReadContacts,
    });

    if (!permissionsLoading && !canReadContacts) {
        return (
            <div className="p-6 text-center text-sm text-text-tertiary">
                You don&apos;t have permission to view contacts
            </div>
        );
    }

    const contacts = data?.pages.flatMap((page) => page.data) ?? [];

    const handleScroll = (event: React.UIEvent<HTMLDivElement>): void => {
        const el = event.currentTarget;
        const nearBottom =
            el.scrollHeight - el.scrollTop - el.clientHeight < LOAD_MORE_THRESHOLD_PX;
        if (nearBottom && hasNextPage && !isFetchingNextPage) void fetchNextPage();
    };

    return (
        <div className={cn('flex min-h-0 flex-col', fill && 'flex-1')}>
            <div className="px-3 pt-3 pb-1">
                <Input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search name or number"
                    aria-label="Search contacts"
                    startIcon={<Search className="size-3.5" />}
                />
            </div>

            <div
                className={cn(
                    'flex min-h-0 flex-col overflow-y-auto',
                    fill ? 'flex-1' : 'max-h-72',
                )}
                onScroll={handleScroll}
            >
                {/* The contacts query is disabled until permissions resolve, so
                    `isLoading` is false during that window — key the skeleton
                    off `permissionsLoading` too (same trap as HistoryPanel). */}
                {isLoading || permissionsLoading ? (
                    <div className="flex flex-col gap-2 p-4">
                        {Array.from({ length: 4 }, (_, i) => (
                            <Skeleton key={i} className="h-10 w-full" />
                        ))}
                    </div>
                ) : contacts.length === 0 ? (
                    <div className="p-6 text-center text-sm text-text-tertiary">
                        {search ? `No matches for “${search}”` : 'No contacts yet'}
                    </div>
                ) : (
                    <div className="flex flex-col divide-y divide-white/40 dark:divide-white/[0.06]">
                        {contacts.map((contact) => (
                            <button
                                key={contact.id}
                                type="button"
                                disabled={Boolean(activeCall) || !contact.phone}
                                title={
                                    activeCall
                                        ? 'Finish the current call first'
                                        : contact.phone
                                          ? `Call ${contact.phone}`
                                          : 'This contact has no phone number'
                                }
                                className="group flex items-center justify-between gap-2 px-4 py-2.5 text-left transition-colors hover:bg-white/50 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:ring-inset focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent dark:hover:bg-white/[0.05]"
                                // Narrowing mirrors the `disabled` guard above —
                                // an email-only contact has no number to dial.
                                onClick={() => {
                                    if (contact.phone) onCall(contact.phone);
                                }}
                            >
                                <div className="flex min-w-0 flex-1 items-center gap-2">
                                    <CallerIdentity
                                        name={contactName(contact)}
                                        number={contact.phone ?? ''}
                                        size="sm"
                                    />
                                </div>
                                <Phone className="size-3.5 shrink-0 text-text-tertiary transition-colors group-hover:text-primary-600 group-disabled:group-hover:text-text-tertiary" />
                            </button>
                        ))}
                    </div>
                )}

                {isFetchingNextPage && (
                    <div className="flex justify-center py-2">
                        <Spinner className="size-4 text-text-tertiary" />
                    </div>
                )}
            </div>
        </div>
    );
}
